import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug } from '../lib/cms';
import ContactForm from '../components/ContactForm';
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from 'lucide-react';

export default function Contact({ isEn }) {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('kontakt');
        setPageData(page);
      } catch (error) {
        console.error('Error loading contact page data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const lang = isEn ? 'en' : 'da';

  const title = pageData?.title?.[lang] || (isEn ? 'Contact Us' : 'Kontakt');
  const content = pageData?.content?.[lang] || '';
  const seoTitle = pageData?.seo_title?.[lang] || `${title} - Fredericia Isolerings-puder`;
  const seoDesc = pageData?.seo_description?.[lang] || '';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
      </Helmet>

      {/* Main Content Grid */}
      <section className="py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full text-left mb-12 space-y-3">
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-black tracking-tight">
              {title}
            </h1>
            <p className="text-gray-600 text-base sm:text-lg max-w-3xl leading-relaxed">
              {isEn 
                ? 'Do you have questions or need a customized quote? Contact us today by phone, email, or fill out the form below.'
                : 'Har du spørgsmål eller ønsker du et skræddersyet tilbud? Kontakt os i dag via telefon, e-mail eller udfyld formularen nedenfor.'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Contact Info Column */}
            <div className="lg:col-span-5 space-y-8 text-left">
              {loading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent" />
              ) : (
                <div 
                  className="text-gray-700 space-y-6 leading-relaxed text-sm sm:text-base prose prose-slate [&_h2]:text-black"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              )}

              {/* Extra Info */}
              <div className="pt-6 border-t border-gray-200 space-y-4">
                <h3 className="font-display font-bold text-black text-lg tracking-tight">
                  {isEn ? 'Opening Hours' : 'Åbningstider'}
                </h3>
                <ul className="space-y-2 text-sm text-gray-600 font-mono">
                  <li className="flex justify-between">
                    <span>{isEn ? 'Monday - Thursday:' : 'Mandag - Torsdag:'}</span>
                    <span className="text-black font-semibold">07:00 - 15:00</span>
                  </li>
                  <li className="flex justify-between">
                    <span>{isEn ? 'Friday:' : 'Fredag:'}</span>
                    <span className="text-black font-semibold">07:00 - 12:00</span>
                  </li>
                  <li className="flex justify-between">
                    <span>{isEn ? 'Saturday - Sunday:' : 'Lørdag - Søndag:'}</span>
                    <span className="text-black font-semibold">{isEn ? 'Closed' : 'Lukket'}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form Column */}
            <div className="lg:col-span-7">
              <ContactForm isEn={isEn} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
